import { ResumeCardTagsData } from 'src/components/ResumeCard/Header/Tags';
import { shouldShowJobSearchStatus } from 'src/components/ResumeCard/Header/Tags/JobSearchStatus';
import { useSelector } from 'src/hooks/useSelector';
import { Platform } from 'src/models/locale';
import type { ResumeCardData } from 'src/models/resumeCard';

import { useSkillsMatchedData } from 'src/components/ResumeCard/Header/Tags/hooks/useSkillsMatchedData';

interface Props {
    isButton: boolean;
    isFirstGift: boolean;
    isFolders: boolean;
    isGifted: boolean;
    isResponse: boolean;
    resume: ResumeCardData;
}

type UseResumeCardTagsDataHook = (props: Props) => ResumeCardTagsData | null;

const useTagsData: UseResumeCardTagsDataHook = ({ isButton, isFirstGift, isFolders, isGifted, isResponse, resume }) => {
    const {
        id: resumeId,
        user: userId,
        sitePlatform: resumePlatform,
        blacklistedByEmployer: isBanned,
    } = resume._attributes;
    const { folders: foldersIdList, testSolutionId, topicId, wannaWork: isWannaWork } = resume;

    const candidateTags = useSelector(
        (state) => state.candidatesList?.candidateTags?.tagsByTopicIds?.[topicId || '']?.tags
    );

    const localePlatform = useSelector((state) => state.locale.platform);
    const isResumeCrossPlatform = localePlatform === Platform.HeadHunter && resumePlatform === Platform.Zarplata;

    const jobSearchStatus = useSelector(
        (state) => state.resumeSearchResult.applicantUserStatuses?.[userId]?.jobSearchStatus?.name
    );

    const skillsMatched = useSkillsMatchedData({ resumeId: Number(resumeId) });

    const skillsVerified = useSelector((state) => state.resumeVerifiedSkills[resumeId]);

    const testSolutions = useSelector((state) => state.candidatesList?.tests?.testSolutions);
    const testResult = testSolutions?.find(({ uidPk }) => uidPk.toString() === testSolutionId)?.score;

    const foldersInvariants = useSelector((state) => state.resumeFolders?.foldersInvariants);

    const data = {
        isBanned,
        isResumeCrossPlatform,
        isWannaWork,
        candidateTags,
        jobSearchStatus: shouldShowJobSearchStatus(jobSearchStatus) && { resumeId, status: jobSearchStatus },
        resumeGifted: isButton && isGifted && { isFirstGift },
        skillsMatched,
        skillsVerified: skillsVerified?.skills.length > 0 && { resumeId, skillsVerified, resume, isResponse },
        testResult: typeof testResult === 'number' && { score: testResult, topicId },
        folderTags: isFolders && foldersIdList && foldersInvariants && { foldersIdList, foldersInvariants },
        relatedProfession: resume.isRelated,
    };

    if (!Object.values(data).some(Boolean)) {
        return null;
    }

    return data as ResumeCardTagsData;
};

export default useTagsData;
